<template>
  <div class="h-full pb-6">
    <!-- Filters section -->
    <div class="px-4 py-2">
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label for="url-filter" class="block text-sm font-medium text-gray-700">URL</label>
          <input
            type="text"
            id="url-filter"
            v-model="urlFilter"
            @input="applyFilters"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Filter op URL"
          />
        </div>
        <div>
          <label for="user-id-filter" class="block text-sm font-medium text-gray-700">Account</label>
          <input
            type="text"
            id="user-id-filter"
            v-model="userIdFilter"
            @input="applyFilters"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Filter op Account"
          />
        </div>
      </div>
      <div class="mt-4">
        <div>
          <label for="date-range" class="block text-sm font-medium text-gray-700">Date Range</label>
          <div class="flex items-center">
            <input
              type="text"
              id="date-range"
              v-model="dateRangeDisplay"
              readonly
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm cursor-pointer"
              @click="toggleDatePicker"
            />
            <button 
              @click="toggleDatePicker" 
              class="ml-2 p-1 rounded-md hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Date Range Picker Popup -->
      <div v-if="showDatePicker" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click="showDatePicker = false"></div>
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Select Date Range
                  </h3>
                  <div class="mt-4">
                    <select 
                      v-model="selectedDatePreset" 
                      @change="applyDatePreset"
                      class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-2 border-gray-200 focus:outline-none sm:text-sm rounded-md"
                    >
                      <option value="custom">Custom Range</option>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="last7Days">Last 7 Days</option>
                      <option value="last30Days">Last 30 Days</option>
                      <option value="thisWeek">This Week</option>
                      <option value="thisMonth">This Month</option>
                    </select>

                    <div class="mt-4 grid grid-cols-2 gap-4">
                      <div>
                        <label for="start-date" class="block text-sm font-medium text-gray-700">Start Date</label>
                        <input 
                          type="date" 
                          id="start-date" 
                          v-model="startDateInput"
                          @change="validateDateRange"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label for="end-date" class="block text-sm font-medium text-gray-700">End Date</label>
                        <input 
                          type="date" 
                          id="end-date" 
                          v-model="endDateInput"
                          @change="validateDateRange"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div v-if="dateRangeError" class="mt-2 text-sm text-red-600">
                      {{ dateRangeError }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button 
                type="button" 
                @click="applyDateRange"
                :disabled="!!dateRangeError"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                :class="{ 'opacity-50 cursor-not-allowed': !!dateRangeError }"
              >
                Apply
              </button>
              <button 
                type="button" 
                @click="showDatePicker = false"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No data message -->
    <div v-if="!dataAvailable && loaded" class="h-full flex justify-center">
      <p class="self-center px-4 -mt-14 text-center">{{ $t('widget.noData') }}</p>
    </div>

    <!-- Data table -->
    <div v-if="dataAvailable && loaded" class="w-full">
      <div class="divide-y divide-gray-300 h-full w-full">
        <table class="divide-y divide-gray-300 w-full mt-2">
          <thead>
          <tr class="flex flex-row px-4">
            <th class="text-left text-l w-2/5 font-semibold text-gray-900">URL</th>
            <th class="text-left text-l w-2/5 font-semibold text-gray-900">Visit Date</th>
            <th class="text-left text-l w-1/5 font-semibold text-gray-900">Account</th>
          </tr>
          </thead>
          <tbody :class="hasLongTitle ? 'divide-y divide-gray-200 h-24rem flex flex-col overflow-y-auto' : 'divide-y divide-gray-200 flex flex-col overflow-y-auto h-[22rem]'">
            <tr v-for="item in items" :key="item.url + item.visitDate" class="flex flex-row px-4 py-2">
              <td class="text-left w-2/5 truncate text-xs" :title="item.url">{{ formatUrl(item.url) }}</td>
              <td class="text-left w-1/5  text-xs">{{ item.visitDate }}</td>
              <td class="text-left w-2/5 text-xs">{{ item.userId || 'Anonymous' }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination controls -->
        <div class="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <div class="flex justify-between flex-1 sm:hidden">
            <button 
              @click="previousPage" 
              :disabled="currentPage === 0"
              :class="[currentPage === 0 ? 'cursor-not-allowed text-gray-400' : 'text-indigo-600 hover:text-indigo-900', 'relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md']"
            >
              Previous
            </button>
            <button 
              @click="nextPage" 
              :disabled="currentPage >= totalPages - 1"
              :class="[currentPage >= totalPages - 1 ? 'cursor-not-allowed text-gray-400' : 'text-indigo-600 hover:text-indigo-900', 'relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md']"
            >
              Next
            </button>
          </div>
          <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p class="text-sm text-gray-700">
                Showing <span class="font-medium">{{ items.length > 0 ? currentPage * pageSize + 1 : 0 }}</span> to <span class="font-medium">{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</span> of <span class="font-medium">{{ totalElements }}</span> results
              </p>
            </div>
            <div>
              <nav class="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button 
                  @click="previousPage" 
                  :disabled="currentPage === 0"
                  :class="[currentPage === 0 ? 'cursor-not-allowed text-gray-400' : 'text-indigo-600 hover:text-indigo-900', 'relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium']"
                >
                  <span class="sr-only">Previous</span>
                  <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
                <span class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  {{ currentPage + 1 }} / {{ totalPages }}
                </span>
                <button 
                  @click="nextPage" 
                  :disabled="currentPage >= totalPages - 1"
                  :class="[currentPage >= totalPages - 1 ? 'cursor-not-allowed text-gray-400' : 'text-indigo-600 hover:text-indigo-900', 'relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium']"
                >
                  <span class="sr-only">Next</span>
                  <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading spinner -->
    <div v-if="!loaded" class="flex h-full items-center justify-center">
      <div class="loader loader--style3">
        <svg id="loader-1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="80px" height="80px" viewBox="0 0 50 50" xml:space="preserve">
          <path class="loadingSpinner h-20" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
            <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/>
          </path>
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, onMounted, ref, watch, computed } from "vue";
import ApiService from "@/services/ApiService";
import moment from "moment";

let apiService = new ApiService();

let props = defineProps({
  update: Number,
  source: String,
  style: Object,
  colorPalette: Object,
  pills: Array,
  type: String,
  hasLongTitle: Boolean
});

const emit = defineEmits(["dataLoaded"]);

let loaded = ref(false);
let items = ref([]);
let dataAvailable = ref(true);
let urlFilter = ref('');
let userIdFilter = ref('');
let debounceTimer = null;
let currentPage = ref(0);
let pageSize = ref(20);
let totalPages = ref(1);
let totalElements = ref(0);

// Date range picker variables
let showDatePicker = ref(false);
let selectedDatePreset = ref('yesterday');
let startDateInput = ref('');
let endDateInput = ref('');
let dateRangeError = ref('');

// Format the date range for display
const dateRangeDisplay = computed(() => {
  if (!startDateInput.value || !endDateInput.value) return '';
  return `${formatDateForDisplay(startDateInput.value)} - ${formatDateForDisplay(endDateInput.value)}`;
});

onMounted(async () => {
  // Set default date range to yesterday and today
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  startDateInput.value = formatDateForInput(yesterday);
  endDateInput.value = formatDateForInput(today);

  // Initialize the API service with the default date range
  apiService.updateDateRange(yesterday, today, 'PREVIOUS_PERIOD');

  await getData();
});

watch(() => props.update, () => {
  getData();
});

function applyFilters() {
  // Debounce the filter application to avoid too many API calls
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    // Reset to first page when filters change
    currentPage.value = 0;
    getData();
  }, 500);
}

// Toggle the date picker popup
function toggleDatePicker() {
  showDatePicker.value = !showDatePicker.value;
}

// Apply a preset date range
function applyDatePreset() {
  const today = new Date();
  let startDate = new Date();
  let endDate = new Date();

  switch (selectedDatePreset.value) {
    case 'today':
      startDate = today;
      endDate = today;
      break;
    case 'yesterday':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;
    case 'last7Days':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 6);
      endDate = today;
      break;
    case 'last30Days':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 29);
      endDate = today;
      break;
    case 'thisWeek': {
      // Start of the week (Monday)
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Sunday
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff);
      endDate = today;
      break;
    }
    case 'thisMonth': {
      // Start of the month
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = today;
      break;
    }
    case 'custom':
      // Keep the current custom range
      return;
  }

  startDateInput.value = formatDateForInput(startDate);
  endDateInput.value = formatDateForInput(endDate);

  validateDateRange();
}

// Validate the date range
function validateDateRange() {
  if (!startDateInput.value || !endDateInput.value) {
    dateRangeError.value = 'Both start and end dates are required';
    return false;
  }

  const startDate = new Date(startDateInput.value);
  const endDate = new Date(endDateInput.value);

  if (startDate > endDate) {
    dateRangeError.value = 'Start date must be before or equal to end date';
    return false;
  }

  // Calculate the difference in days
  const diffTime = Math.abs(endDate - startDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays > 30) {
    dateRangeError.value = 'Date range cannot exceed 30 days';
    return false;
  }

  dateRangeError.value = '';
  return true;
}

// Apply the selected date range
function applyDateRange() {
  if (!validateDateRange()) {
    return;
  }

  const startDate = new Date(startDateInput.value);
  const endDate = new Date(endDateInput.value);

  // Update the API service with the new date range
  apiService.updateDateRange(startDate, endDate, 'PREVIOUS_PERIOD');

  // Close the date picker and fetch data
  showDatePicker.value = false;
  currentPage.value = 0;
  getData();
}

// Format date for input field (YYYY-MM-DD)
function formatDateForInput(date) {
  return moment(date).format('YYYY-MM-DD');
}

// Format date for display (DD/MM/YYYY)
function formatDateForDisplay(dateString) {
  return moment(dateString).format('DD/MM/YYYY');
}

function previousPage() {
  if (currentPage.value > 0) {
    currentPage.value--;
    getData();
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value - 1) {
    currentPage.value++;
    getData();
  }
}

function formatUrl(url) {
  try {
    // If the URL doesn't start with http or https, return it as is
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return url;
    }

    // Parse the URL to extract the pathname
    const urlObj = new URL(url);
    // Return only the path part (removing domain)
    return urlObj.pathname;
  } catch (e) {
    // If there's an error parsing the URL, return the original
    return url;
  }
}

async function getData() {
  loaded.value = false;
  items.value = [];

  try {
    const response = await apiService.getPageVisitDetails(
      urlFilter.value || null,
      userIdFilter.value || null,
      currentPage.value,
      pageSize.value
    );

    if (response && response.data) {
      items.value = response.data;
      dataAvailable.value = items.value.length > 0;

      // Update pagination information if available
      if (response.pagination) {
        totalPages.value = response.pagination.totalPages;
        totalElements.value = response.pagination.totalElements;
        // Ensure current page is valid
        if (currentPage.value >= totalPages.value && totalPages.value > 0) {
          currentPage.value = totalPages.value - 1;
          // Fetch data again with the corrected page
          await getData();
          return;
        }
      } else {
        // If pagination info is not available, set defaults
        totalPages.value = 1;
        totalElements.value = items.value.length;
      }
    } else {
      dataAvailable.value = false;
      totalPages.value = 1;
      totalElements.value = 0;
    }
  } catch (error) {
    console.error("Error fetching page visit details:", error);
    dataAvailable.value = false;
    totalPages.value = 1;
    totalElements.value = 0;
  } finally {
    loaded.value = true;
    emit("dataLoaded");
  }
}
</script>
